import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { ApiError } from '../../api/client';
import { ApiImage } from '../../components/ui/ApiImage';
import { useAuth } from '../auth/AuthContext';
import { BiometricLock } from './BiometricLock';
import {
  changePassword,
  confirmTotp,
  disableTotp,
  fetchSecurity,
  fetchSessions,
  passkeysSupported,
  registerPasskey,
  removePasskey,
  resetRecoveryCodes,
  revokeSession,
  startTotp,
} from '../auth/api';

function when(value: string | null): string {
  return value ? new Date(value).toLocaleString() : '—';
}

/**
 * Recovery codes, shown exactly once.
 *
 * Printed rather than stored: the whole point is that they work when the phone
 * that holds the authenticator does not, so leaving them in a password manager
 * on that phone would defeat them. The screen therefore pushes towards paper and
 * says plainly that this is the only time they will be visible.
 */
function RecoveryCodes({ codes, onDone }: { codes: string[]; onDone: () => void }) {
  const { t } = useTranslation();

  return (
    <div className="security__codes">
      <h4>{t('security.recovery.title')}</h4>
      <p className="security__warning">{t('security.recovery.onlyOnce')}</p>
      <ol>
        {codes.map((code) => (
          <li key={code}>
            <code>{code}</code>
          </li>
        ))}
      </ol>
      <div className="security__actions">
        <button type="button" onClick={() => window.print()}>
          {t('security.recovery.print')}
        </button>
        <button type="button" onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}>
          {t('common.copy')}
        </button>
        <button type="button" className="primary" onClick={onDone}>
          {t('security.recovery.saved')}
        </button>
      </div>
    </div>
  );
}

function TotpSection() {
  const { t } = useTranslation();
  const { user, refresh } = useAuth();
  const queryClient = useQueryClient();

  const security = useQuery({ queryKey: ['security'], queryFn: fetchSecurity });
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['security'] });
    void refresh();
  };

  const begin = useMutation({
    mutationFn: startTotp,
    onSuccess: (data) => {
      setSecret(data.secret);
      setError(null);
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.code : 'unknown'),
  });

  const confirm = useMutation({
    mutationFn: () => confirmTotp(code),
    onSuccess: (data) => {
      setCodes(data.codes);
      setSecret(null);
      setCode('');
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.code : 'unknown'),
  });

  const turnOff = useMutation({
    mutationFn: () => disableTotp(code),
    onSuccess: () => {
      setCode('');
      setError(null);
      invalidate();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.code : 'unknown'),
  });

  const regenerate = useMutation({
    mutationFn: resetRecoveryCodes,
    onSuccess: (data) => {
      setCodes(data.codes);
      invalidate();
    },
  });

  const enrolled = security.data?.totp_enrolled ?? false;

  if (codes) return <RecoveryCodes codes={codes} onDone={() => setCodes(null)} />;

  return (
    <section className="security__block">
      <header>
        <h3>{t('security.totp.title')}</h3>
        <span className={enrolled ? 'badge badge--on' : 'badge'}>
          {enrolled ? t('security.on') : t('security.off')}
        </span>
      </header>
      <p className="security__hint">{t('security.totp.hint')}</p>

      {user?.mfa_required && !enrolled && (
        <p className="security__warning">{t('security.totp.requiredByAdmin')}</p>
      )}

      {!enrolled && !secret && (
        <button type="button" onClick={() => begin.mutate()} disabled={begin.isPending}>
          {t('security.totp.start')}
        </button>
      )}

      {secret && (
        <div className="security__enrol">
          {/* Rendered by the server: the front end has no QR library, and the
              code that draws one on every label already exists there. */}
          <ApiImage
            className="security__qr"
            path={`/auth/totp/qr?ts=${encodeURIComponent(secret.slice(0, 6))}`}
            alt={t('security.totp.qrAlt')}
          />
          <div>
            <p>{t('security.totp.scan')}</p>
            <p className="security__secret">
              <code>{secret}</code>
            </p>
            <label htmlFor="totp-code">{t('security.totp.enterCode')}</label>
            <input
              id="totp-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <button
              type="button"
              className="primary"
              onClick={() => confirm.mutate()}
              disabled={confirm.isPending || code.length < 6}
            >
              {t('security.totp.confirm')}
            </button>
          </div>
        </div>
      )}

      {enrolled && (
        <div className="security__enrolled">
          <p>
            {t('security.recovery.left', { count: security.data?.recovery_codes_left ?? 0 })}
          </p>
          <div className="security__actions">
            <button type="button" onClick={() => regenerate.mutate()}>
              {t('security.recovery.regenerate')}
            </button>
          </div>

          {!user?.mfa_required && (
            <div className="security__disable">
              <label htmlFor="totp-off">{t('security.totp.disableHint')}</label>
              <input
                id="totp-off"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              <button type="button" onClick={() => turnOff.mutate()} disabled={code.length < 6}>
                {t('security.totp.disable')}
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="security__warning" role="alert">
          {t(`security.errors.${error}`, { defaultValue: t('security.errors.unknown') })}
        </p>
      )}
    </section>
  );
}

function PasskeysSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const security = useQuery({ queryKey: ['security'], queryFn: fetchSecurity });
  const [nickname, setNickname] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => registerPasskey(nickname.trim() || null),
    onSuccess: () => {
      setNickname('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['security'] });
    },
    onError: (e: unknown) => {
      // The browser's own prompt was dismissed. Not worth an error message.
      if (e instanceof Error && e.name === 'NotAllowedError') return;
      setError(e instanceof ApiError ? e.code : 'unknown');
    },
  });

  const drop = useMutation({
    mutationFn: removePasskey,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['security'] }),
  });

  if (!passkeysSupported()) {
    return (
      <section className="security__block">
        <header>
          <h3>{t('security.passkeys.title')}</h3>
        </header>
        <p className="security__hint">{t('security.passkeys.unsupported')}</p>
      </section>
    );
  }

  const keys = security.data?.passkeys ?? [];

  return (
    <section className="security__block">
      <header>
        <h3>{t('security.passkeys.title')}</h3>
      </header>
      <p className="security__hint">{t('security.passkeys.hint')}</p>
      <p className="security__hint">{t('security.passkeys.modeHint')}</p>

      {keys.length > 0 && (
        <div className="scroll-x">
          <table className="security__table">
            <thead>
              <tr>
                <th>{t('security.passkeys.name')}</th>
                <th>{t('security.passkeys.mode')}</th>
                <th>{t('security.passkeys.added')}</th>
                <th>{t('security.passkeys.lastUsed')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr key={key.id}>
                  <td>{key.nickname || t('security.passkeys.unnamed')}</td>
                  <td>
                    {/* Null means the key was registered before this was
                        recorded, or never said. Both read as "needs the address",
                        because that is the assumption that cannot mislead. */}
                    {key.discoverable ? (
                      <span className="badge badge--on">{t('security.passkeys.passwordless')}</span>
                    ) : (
                      <span className="badge">{t('security.passkeys.needsEmail')}</span>
                    )}
                  </td>
                  <td>{when(key.created_at)}</td>
                  <td>{when(key.last_used_at)}</td>
                  <td>
                    <button type="button" onClick={() => drop.mutate(key.id)}>
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="security__add-key">
        <input
          placeholder={t('security.passkeys.namePlaceholder')}
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
        />
        <button type="button" onClick={() => add.mutate()} disabled={add.isPending}>
          {t('security.passkeys.add')}
        </button>
      </div>

      {error && (
        <p className="security__warning" role="alert">
          {t(`security.errors.${error}`, { defaultValue: t('security.errors.unknown') })}
        </p>
      )}
    </section>
  );
}

function SessionsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const sessions = useQuery({ queryKey: ['sessions'], queryFn: fetchSessions });

  const drop = useMutation({
    mutationFn: revokeSession,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  return (
    <section className="security__block">
      <header>
        <h3>{t('security.sessions.title')}</h3>
      </header>
      <p className="security__hint">{t('security.sessions.hint')}</p>

      <div className="scroll-x">
        <table className="security__table">
          <thead>
            <tr>
              <th>{t('security.sessions.device')}</th>
              <th>{t('security.sessions.address')}</th>
              <th>{t('security.sessions.lastSeen')}</th>
              <th>{t('security.sessions.factors')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(sessions.data ?? []).map((row) => (
              <tr key={row.id} className={row.current ? 'is-current' : undefined}>
                <td>
                  {row.current && <strong>{t('security.sessions.thisDevice')} · </strong>}
                  <span title={row.user_agent ?? ''}>{row.user_agent ?? row.client}</span>
                </td>
                <td>{row.ip ?? '—'}</td>
                <td>{when(row.last_seen_at)}</td>
                <td>{row.amr.map((factor) => t(`security.factors.${factor}`, factor)).join(' + ')}</td>
                <td>
                  {!row.current && (
                    <button type="button" onClick={() => drop.mutate(row.id)}>
                      {t('security.sessions.revoke')}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PasswordSection() {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [state, setState] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setState('ok');
    },
    onError: (e: unknown) => setState(e instanceof ApiError ? e.code : 'unknown'),
  });

  return (
    <section className="security__block">
      <header>
        <h3>{t('security.password.title')}</h3>
      </header>
      {/* Said before the button is pressed, not after: it changes whether
          somebody does this from the phone they are about to walk away with. */}
      <p className="security__hint">{t('security.password.endsSessions')}</p>

      <div className="security__password">
        <label htmlFor="pw-current">{t('security.password.current')}</label>
        <input
          id="pw-current"
          type="password"
          autoComplete="current-password"
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <label htmlFor="pw-next">{t('security.password.next')}</label>
        <input
          id="pw-next"
          type="password"
          autoComplete="new-password"
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <button
          type="button"
          onClick={() => change.mutate()}
          disabled={change.isPending || next.length < 10 || !current}
        >
          {t('security.password.submit')}
        </button>
      </div>

      {state === 'ok' && <p className="security__ok">{t('security.password.changed')}</p>}
      {state && state !== 'ok' && (
        <p className="security__warning" role="alert">
          {t(`security.errors.${state}`, { defaultValue: t('security.errors.unknown') })}
        </p>
      )}
    </section>
  );
}

/** Everything a person can do about their own account's security. */
export function SecurityPage() {
  const { t } = useTranslation();

  return (
    <div className="security">
      <h2>{t('nav.security')}</h2>
      {/* First, and invisible everywhere else: in the application this is the
          thing somebody came here to switch on. */}
      <BiometricLock />
      <TotpSection />
      <PasskeysSection />
      <SessionsSection />
      <PasswordSection />
    </div>
  );
}
